import React, { useEffect, useMemo, useRef, useState } from 'react'
import { formatScorecardDecimal } from 'helpers/scorecardFormat'
import { findMhReportCardByCandidateId } from 'gql/query'
import centaLogo from 'assets/centaR-removebg-preview.png'
import mhGovtLogo from 'assets/mh-govt-logo.png'
import mhCoverPhotoLeft from 'assets/mh-cover-photo-left.png'
import mhCoverPhotoCenter from 'assets/mh-cover-photo-center.png'
import mhCoverPhotoRight from 'assets/mh-cover-photo-right.png'
import Notfound from 'assets/404 error with people holding the numbers-rafiki.png'
import mhDriveStats from 'assets/mhDriveStats.json'
import './index.less'
import './mhReport.less'

/**
 * Maharashtra (MH) scorecard — TNA template with cover plus all inner report pages,
 * rendered from Mh_report_card plus precomputed drive statistics (assets/mhDriveStats.json).
 */

interface Props {
  candidate?: string
}

const SCORECARD_WIDTH_PX = 794
const MOBILE_BREAKPOINT_PX = 768
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const MH_TNA_DATE = '08-08-2026'
const MH_CYCLE_LABEL = 'AUGUST 2026'
const MH_PAGE_COUNT = 5

const NEP_FOOTER_TEXT =
  'The National Education Policy (NEP) 2020 emphasizes the importance of well-defined professional standards for teachers. This report provides you with insights aligned with the National Professional Standards for Teachers (NPST), helping you grow as an educator.'

const CHART_ORANGE_TOP = '#FFE4B8'
const CHART_ORANGE_BOTTOM = '#F5A04A'
const CHART_ORANGE = '#F5A04A'
const LINE_ORANGE = '#D96B12'

interface GroupStats {
  count: number
  overall: number
  sections: Record<string, number>
  blooms: Record<string, number>
  scores: number[]
}

const driveStats: any = mhDriveStats

// Every percentage on this scorecard is shown rounded to a whole number; the
// underlying data keeps full precision.
const formatPercent = (value: unknown) => `${Math.round(Number(value) || 0)}%`

const toPercentNumber = (value: unknown) => {
  const numeric = Number(value)
  if (Number.isNaN(numeric)) {
    return 0
  }
  return Math.max(0, Math.min(100, numeric))
}

const parseJsonArray = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

const ABBREVIATION_SKIP_WORDS = new Set(['of', 'and', 'the', 'in', 'for'])

const abbreviate = (name: string) => {
  const words = String(name || '').replace(/[()]/g, ' ').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return ''
  }
  if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase()
  }
  let abbreviation = ''
  words.forEach(word => {
    if (word === '&') {
      abbreviation += '&'
      return
    }
    if (ABBREVIATION_SKIP_WORDS.has(word.toLowerCase())) {
      return
    }
    abbreviation += word[0].toUpperCase()
  })
  return abbreviation
}

// Profile values arrive as free text in mixed cases (ALL-CAPS names, "school" in
// lowercase inside venue strings). Render them as "First letter caps, rest small".
// Identifier fields (employee ID, UDISE) are left exactly as stored.
const toProperCase = (value: unknown, keepAcronyms = false): string => {
  const text = String(value ?? '')
  if (keepAcronyms) {
    // School names carry acronyms (SKC, ZP) that read as typos when flattened to "Skc".
    return text
      .split(/(\s+)/)
      .map(token => (/^[A-Z]{2,4}$/.test(token) ? token : toProperCase(token)))
      .join('')
  }
  return text
    .toLowerCase()
    .replace(/(^|[\s\-/(,.&])([a-z])/g, (_match, separator, letter) => separator + letter.toUpperCase())
}

const sectionShortName = (name: string) => {
  const inParens = /\(([^)]+)\)/.exec(String(name || ''))
  return inParens ? inParens[1] : abbreviate(name)
}

const sectionFullName = (name: string) => String(name || '').replace(/\s*\([^)]*\)/, '').trim()

// Section names arrive from the DB in mixed case ("Professional and core competencies").
// Title Case them for display, leaving joining words lowercase.
const TITLE_CASE_SKIP = new Set(['and', 'of', 'the', 'in', 'for', 'to', 'with'])

const toTitleCase = (name: string) =>
  String(name || '')
    .split(' ')
    .map((word, index) => {
      const lower = word.toLowerCase()
      if (index > 0 && TITLE_CASE_SKIP.has(lower)) {
        return lower
      }
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')

// Full display name incl. the "(SE)" style suffix, e.g. "Professional and Core Competencies (PC)"
const sectionDisplayName = (name: string) => {
  const suffix = /\(([^)]+)\)/.exec(String(name || ''))
  const base = toTitleCase(sectionFullName(name))
  return suffix ? `${base} (${suffix[1]})` : base
}

interface ChartItem {
  label: string
  value: number
  line?: number
}

interface ChartProps {
  items: ChartItem[]
  width?: number
  height?: number
  showLine?: boolean
  compact?: boolean
  lineEndLabel?: string
}

// Percentage bar chart (0-100 axis) matching the TNA template style.
const PercentBarChart: React.FC<ChartProps> = ({
  items,
  width = 620,
  height = 250,
  showLine = false,
  compact = false,
  lineEndLabel,
}) => {
  const padLeft = compact ? 42 : 48
  // Leave room on the right for the score value printed at the end of the line.
  const padRight = lineEndLabel ? 44 : 12
  const padTop = compact ? 22 : 26
  const padBottom = compact ? 30 : 34
  const plotWidth = width - padLeft - padRight
  const plotHeight = height - padTop - padBottom
  const slotWidth = items.length > 0 ? plotWidth / items.length : plotWidth
  const barWidth = Math.min(compact ? 44 : 58, slotWidth * 0.55)
  const yFor = (value: number) => padTop + plotHeight - (toPercentNumber(value) / 100) * plotHeight
  const labelFont = compact ? 11 : 13
  const axisFont = compact ? 10 : 11.5
  const xFont = compact ? 10.5 : 12.5
  const gradientId = `mh-bar-gradient-${width}-${height}-${compact ? 'c' : 'f'}-${items.length}`

  const linePoints = items
    .map((item, index) => `${padLeft + slotWidth * index + slotWidth / 2},${yFor(item.line ?? 0)}`)
    .join(' ')

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fontFamily="'Nunito Sans', sans-serif">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={CHART_ORANGE_TOP} />
          <stop offset="100%" stopColor={CHART_ORANGE_BOTTOM} />
        </linearGradient>
      </defs>
      {[0, 25, 50, 75, 100].map(tick => (
        <g key={tick}>
          <line
            x1={padLeft}
            y1={yFor(tick)}
            x2={width - padRight}
            y2={yFor(tick)}
            stroke="#c9cfd8"
            strokeWidth={1}
            strokeDasharray={tick === 0 ? undefined : '4 4'}
          />
          <text x={padLeft - 6} y={yFor(tick) + 3.5} textAnchor="end" fontSize={axisFont} fill="#5f6b7a">
            {tick}%
          </text>
        </g>
      ))}

      {items.map((item, index) => {
        const centerX = padLeft + slotWidth * index + slotWidth / 2
        const barTop = yFor(item.value)
        return (
          <g key={`bar-${item.label}-${index}`}>
            <rect
              x={centerX - barWidth / 2}
              y={barTop}
              width={barWidth}
              height={Math.max(0, yFor(0) - barTop)}
              fill={`url(#${gradientId})`}
            />
            <text x={centerX} y={height - padBottom + (compact ? 14 : 17)} textAnchor="middle" fontSize={xFont} fontWeight={700} fill="#222222">
              {item.label}
            </text>
          </g>
        )
      })}

      {showLine && items.length > 0 && (
        <g>
          <polyline points={linePoints} fill="none" stroke={LINE_ORANGE} strokeWidth={3} />
          {items.map((item, index) => (
            <circle
              key={`dot-${index}`}
              cx={padLeft + slotWidth * index + slotWidth / 2}
              cy={yFor(item.line ?? 0)}
              r={4.5}
              fill={LINE_ORANGE}
            />
          ))}
          {lineEndLabel && (
            <text
              x={padLeft + slotWidth * (items.length - 1) + slotWidth / 2 + 12}
              y={yFor(items[items.length - 1].line ?? 0) + 4}
              textAnchor="start"
              fontSize={labelFont}
              fontWeight={800}
              fill={LINE_ORANGE}
            >
              {lineEndLabel}
            </text>
          )}
        </g>
      )}

      {/* Value labels render last: on line charts they sit inside the bar in white, and are
          nudged clear of the score line so the line never runs through the digits. */}
      {items.map((item, index) => {
        const centerX = padLeft + slotWidth * index + slotWidth / 2
        const barTop = yFor(item.value)
        const barBottom = yFor(0)
        const barHeight = Math.max(0, barBottom - barTop)
        const insideLabel = showLine && barHeight > labelFont * 2.2
        // Centred in the bar (baseline sits ~1/3 of the cap height below the midpoint).
        let labelY = insideLabel ? barTop + barHeight / 2 + labelFont / 3 : barTop - 6

        if (insideLabel) {
          const lineY = yFor(item.line ?? 0)
          const textTop = labelY - labelFont
          if (lineY > textTop - 4 && lineY < labelY + 4) {
            const below = lineY + labelFont + 7
            const above = lineY - 7
            if (below < barBottom - 4) {
              labelY = below
            } else if (above - labelFont > barTop + 2) {
              labelY = above
            }
          }
        }

        return (
          <text
            key={`value-${item.label}-${index}`}
            x={centerX}
            y={labelY}
            textAnchor="middle"
            fontSize={labelFont}
            fontWeight={800}
            fill={insideLabel ? '#7a3e08' : CHART_ORANGE}
          >
            {formatPercent(item.value)}
          </text>
        )
      })}
    </svg>
  )
}

const TnaPageHeader: React.FC = () => (
  <div className="mh-tna-header">
    <div className="mh-tna-header-logo">
      <img src={mhGovtLogo} alt="Government of Maharashtra" />
    </div>
    <div className="mh-tna-header-title">Teacher Scorecard</div>
    <div className="mh-tna-header-logo">
      <img src={centaLogo} alt="CENTA logo" />
    </div>
  </div>
)

const NepFooter: React.FC = () => <div className="mh-nep-footer">{NEP_FOOTER_TEXT}</div>

const MhPageNumber: React.FC<{ page: number }> = ({ page }) => (
  <div className="mh-page-number">
    {page} / {MH_PAGE_COUNT}
  </div>
)

const CompareLegend: React.FC<{ lineLabel: string; barLabel: string }> = ({ lineLabel, barLabel }) => (
  <div className="mh-compare-legend">
    <div className="mh-compare-legend-item">
      <span className="mh-legend-line-swatch" />
      {lineLabel}
    </div>
    <div className="mh-compare-legend-item">
      <span className="mh-legend-swatch" />
      {barLabel}
    </div>
  </div>
)

const MhCandidateReport: React.FC<Props> = ({ candidate }) => {
  const [loading, setLoading] = useState<boolean>(Boolean(candidate))
  const [reportData, setReportData] = useState<any>(null)
  const [validCandidate, setValidCandidate] = useState<boolean>(true)
  const [mobileScale, setMobileScale] = useState(1)
  const [mobileView, setMobileView] = useState(false)
  const scaleRef = useRef({ isMobile: false, scale: 1 })

  useEffect(() => {
    document.documentElement.removeAttribute('data-scorecard-ready')

    if (!candidate || !UUID_REGEX.test(candidate)) {
      setLoading(false)
      setValidCandidate(false)
      return
    }

    setLoading(true)
    setValidCandidate(true)
    setReportData(null)

    const loadReport = async () => {
      try {
        const data: any = await findMhReportCardByCandidateId(candidate)
        const reportCard = data?.Mh_report_card?.[0]
        if (reportCard?.id) {
          setReportData(reportCard)
        } else {
          setValidCandidate(false)
        }
      } catch (error) {
        console.error('Failed to load MH scorecard', error)
        setValidCandidate(false)
      } finally {
        setLoading(false)
      }
    }
    loadReport()

    return () => {
      document.documentElement.removeAttribute('data-scorecard-ready')
    }
  }, [candidate])

  useEffect(() => {
    if (loading) {
      document.documentElement.removeAttribute('data-scorecard-ready')
      return
    }

    if (validCandidate && reportData) {
      const markReady = () => document.documentElement.setAttribute('data-scorecard-ready', 'true')
      // rAF fires after paint, but Chrome throttles it in background tabs during batch
      // capture — the timer is the fallback so the flag is always published.
      const frameId = requestAnimationFrame(markReady)
      const timerId = setTimeout(markReady, 300)
      return () => {
        cancelAnimationFrame(frameId)
        clearTimeout(timerId)
      }
    }

    document.documentElement.removeAttribute('data-scorecard-ready')
  }, [loading, validCandidate, reportData])

  useEffect(() => {
    const updateScale = () => {
      const isPdfCapture = new URLSearchParams(window.location.search).get('pdfCapture') === '1'
      const isMobile = !isPdfCapture && window.innerWidth < MOBILE_BREAKPOINT_PX
      const scale = isMobile ? Math.min(1, (window.innerWidth - 16) / SCORECARD_WIDTH_PX) : 1
      const rounded = Math.round(scale * 1000) / 1000
      if (scaleRef.current.isMobile === isMobile && Math.abs(scaleRef.current.scale - rounded) < 0.001) {
        return
      }
      scaleRef.current = { isMobile, scale: rounded }
      setMobileView(isMobile)
      setMobileScale(rounded)
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [])

  const sections = useMemo(() => parseJsonArray(reportData?.section_wise_details), [reportData])
  const blooms = useMemo(() => parseJsonArray(reportData?.blooms_details), [reportData])

  const overallPercentage = toPercentNumber(reportData?.percentage_obtained)

  const stateStats: GroupStats = driveStats.state
  const geo = candidate ? driveStats.candidateGeo?.[candidate] : null
  const blockStats: GroupStats | null = (geo?.b && driveStats.blocks?.[geo.b]) || null
  const districtStats: GroupStats | null = (geo?.d && driveStats.districts?.[geo.d]) || null

  // School-level comparison was dropped on request — most schools sent only one or two
  // teachers, so a "school average" was often just the candidate's own score.
  const comparisonLevels: { label: string; stats: GroupStats | null }[] = [
    { label: 'Block', stats: blockStats },
    { label: 'District', stats: districtStats },
    { label: 'State', stats: stateStats },
  ]

  const sectionChartItems: ChartItem[] = sections.map((section: any) => ({
    label: sectionShortName(section.name),
    value: toPercentNumber(section.percentage),
  }))

  const bloomChartItems: ChartItem[] = blooms.map((bloom: any) => ({
    label: bloom.name,
    value: toPercentNumber(bloom.percentage),
  }))

  const overallComparisonItems: ChartItem[] = comparisonLevels.map(level => ({
    label: level.label,
    value: toPercentNumber(level.stats?.overall ?? 0),
    line: overallPercentage,
  }))

  const sectionComparisonItemsFor = (groupStats: GroupStats | null): ChartItem[] =>
    sections.map((section: any) => ({
      label: sectionShortName(section.name),
      value: toPercentNumber(groupStats?.sections?.[section.name] ?? 0),
      line: toPercentNumber(section.percentage),
    }))

  const themeLegendBySection = useMemo(
    () =>
      sections
        .map((section: any) => {
          const seen = new Set<string>()
          const themes: { abbr: string; name: string }[] = []
          ;(section.report_theme_details || []).forEach((theme: any) => {
            const abbr = abbreviate(theme.name)
            if (!seen.has(abbr)) {
              seen.add(abbr)
              themes.push({ abbr, name: toTitleCase(theme.name) })
            }
          })
          return { section: sectionDisplayName(section.name), themes }
        })
        .filter((group: any) => group.themes.length > 0),
    [sections]
  )

  const sectionLegendText = sections
    .map((section: any) => `${sectionShortName(section.name)} - ${toTitleCase(sectionFullName(section.name))}`)
    .join(';  ')

  const yourSectionScoresText = sections
    .map((section: any) => `${sectionShortName(section.name)}: ${formatPercent(section.percentage)}`)
    .join('   ')
  const yourScoreLine = `Your Score — ${yourSectionScoresText}`

  if (loading) {
    return (
      <div className="scorecard-outer">
        <div className="scorecard-pending">
          <p style={{ fontSize: '18px', color: '#0c5692', textAlign: 'center' }}>
            Please wait, we are preparing your scorecard...
          </p>
        </div>
      </div>
    )
  }

  if (!validCandidate || !reportData) {
    return (
      <div className="scorecard-outer">
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', margin: 0 }}>
          <img src={Notfound} alt="scorecard not found" width="50%" height="50%" />
        </div>
      </div>
    )
  }

  return (
    <div className="scorecard-outer">
      <div className="scorecard-viewport">
        <div className="scorecard-scale-shell">
          <div
            className="scorecard-document"
            style={
              mobileView
                ? ({ transform: `scale(${mobileScale})`, transformOrigin: 'top center' } as React.CSSProperties)
                : undefined
            }
          >
            {/* ---------------- Page 1: cover ---------------- */}
            <div className="scorecard-page mh-tna-page mh-cover">
              <div className="mh-cover-top">
                <img className="mh-cover-seal" src={mhGovtLogo} alt="Government of Maharashtra" />
                <h1 className="mh-cover-title">
                  TEACHER TNA
                  <br />
                  SCORECARD
                </h1>
                <div className="mh-cover-cycle">{MH_CYCLE_LABEL}</div>
                <div className="mh-cover-powered">
                  <span>Powered by CENTA</span>
                  <img src={centaLogo} alt="CENTA logo" />
                </div>
              </div>
              <div className="mh-cover-wave">
                <div className="mh-cover-wave-light" />
                <div className="mh-cover-wave-dark" />
              </div>
              <img className="mh-cover-photo mh-cover-photo--left" src={mhCoverPhotoLeft} alt="classroom" />
              <img className="mh-cover-photo mh-cover-photo--right" src={mhCoverPhotoRight} alt="students" />
              <img className="mh-cover-photo mh-cover-photo--center" src={mhCoverPhotoCenter} alt="teachers in courtyard" />
              <div className="mh-cover-footer" />
              <MhPageNumber page={1} />
            </div>

            {/* ---------------- Page 2: profile + overall + section performance ---------------- */}
            <div className="scorecard-page mh-tna-page">
              <TnaPageHeader />

              <div className="mh-tna-title">
                <h2>Teacher Profile</h2>
                <span>TNA Date : {MH_TNA_DATE}</span>
              </div>

              <div className="mh-profile-table">
                <div className="mh-profile-row">
                  <div className="mh-profile-label">Employee ID</div>
                  <div className="mh-profile-value">{reportData?.employee_id || '—'}</div>
                </div>
                <div className="mh-profile-row">
                  <div className="mh-profile-label">Teacher Name</div>
                  <div className="mh-profile-value">{toProperCase(reportData?.candidate_name)}</div>
                </div>
                <div className="mh-profile-row">
                  <div className="mh-profile-label">Subject Track</div>
                  <div className="mh-profile-value">{toProperCase(String(reportData?.paper_name || '').split('-TQ-')[0] || reportData?.paper_name)}</div>
                </div>
                <div className="mh-profile-row">
                  <div className="mh-profile-label">UDISE Code</div>
                  <div className="mh-profile-value">{reportData?.udise_code || '—'}</div>
                </div>
                <div className="mh-profile-row">
                  <div className="mh-profile-label">Block</div>
                  <div className="mh-profile-value">{geo?.b ? toProperCase(String(geo.b).split('|')[1]) : '—'}</div>
                </div>
                <div className="mh-profile-row">
                  <div className="mh-profile-label">District</div>
                  <div className="mh-profile-value">{geo?.d ? toProperCase(geo.d) : '—'}</div>
                </div>
              </div>

              <div className="mh-overall-score">
                Overall Score: {formatScorecardDecimal(reportData?.total_obtained_score)}/
                {formatScorecardDecimal(reportData?.paper_total_score)}
              </div>

              <div className="mh-chip-row">
                <div className="mh-chip">
                  <label>Overall Score %</label>
                  <strong>{formatPercent(overallPercentage)}</strong>
                </div>
                <div className="mh-chip">
                  <label>Block Average %</label>
                  <strong>{formatPercent(blockStats?.overall ?? stateStats?.overall)}</strong>
                </div>
                <div className="mh-chip">
                  <label>Badge Winner</label>
                  <strong>{reportData?.badge_winner ? 'Yes' : 'No'}</strong>
                </div>
              </div>

              <div className="mh-criteria">
                Badge Eligibility Criteria: Teacher should have an overall score of 50% or more and 30% or more in
                individual sections (categorized based on competencies)
              </div>

              <div className="mh-tna-title mh-tna-title--center">
                <h2>Section Wise Performance</h2>
              </div>
              <div className="mh-chart-card">
                <div className="mh-chart-block">
                  <PercentBarChart items={sectionChartItems} width={660} height={230} />
                </div>
                <div className="mh-chart-legend-line">{sectionLegendText}</div>
              </div>

              <NepFooter />
              <MhPageNumber page={2} />
            </div>

            {/* ---------------- Page 3: report themes ---------------- */}
            <div className="scorecard-page mh-tna-page">
              <TnaPageHeader />

              <div className="mh-tna-title mh-tna-title--center">
                <h2>Section level - Report themes graphs</h2>
              </div>

              <div className="mh-mini-chart-grid">
                {sections.map((section: any, sectionIndex: number) => (
                  <div className="mh-mini-chart" key={section.id || sectionIndex}>
                    <h3>{sectionDisplayName(section.name)}</h3>
                    <div className="mh-chart-block">
                      <PercentBarChart
                        compact
                        width={340}
                        height={215}
                        items={(section.report_theme_details || []).map((theme: any) => ({
                          label: abbreviate(theme.name),
                          value: toPercentNumber(theme.percentage),
                        }))}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mh-abbr-legend">
                {themeLegendBySection.map((group: any) => (
                  <div className="mh-abbr-group" key={group.section}>
                    <h4>{group.section}</h4>
                    {group.themes.map((item: any) => (
                      <div className="mh-abbr-item" key={item.abbr}>
                        <strong>{item.abbr}</strong> - {item.name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <NepFooter />
              <MhPageNumber page={3} />
            </div>

            {/* ---------------- Page 4: Bloom's + overall peer comparison ---------------- */}
            <div className="scorecard-page mh-tna-page">
              <TnaPageHeader />

              <div className="mh-tna-title mh-tna-title--center">
                <h2>Performance based on Bloom's Taxonomy</h2>
              </div>
              <div className="mh-chart-card">
                <div className="mh-chart-block">
                  <PercentBarChart items={bloomChartItems} width={640} height={245} />
                </div>
                <div className="mh-chart-legend-line">
                  <strong>HOTS</strong> - Higher Order Thinking Skills
                </div>
              </div>

              <div className="mh-tna-title mh-tna-title--center" style={{ marginTop: '20px' }}>
                <h2>Compare your performance with peers in your state</h2>
              </div>
              <div className="mh-chart-card">
                <div className="mh-tna-title mh-tna-title--center" style={{ margin: '2px 0 0' }}>
                  <h2 style={{ fontSize: '17px' }}>Overall Score Comparison</h2>
                </div>
                <div className="mh-your-score-note">Your Score: {formatPercent(overallPercentage)}</div>
                <CompareLegend lineLabel="Your Score %" barLabel="Subject Average %" />
                <div className="mh-chart-block">
                  <PercentBarChart
                    items={overallComparisonItems}
                    width={640}
                    height={245}
                    showLine
                    lineEndLabel={formatPercent(overallPercentage)}
                  />
                </div>
              </div>

              <NepFooter />
              <MhPageNumber page={4} />
            </div>

            {/* ---------------- Page 5: section wise comparison ---------------- */}
            <div className="scorecard-page mh-tna-page">
              <TnaPageHeader />

              <div className="mh-chart-card">
                <div className="mh-tna-title mh-tna-title--center">
                  <h2>Section Wise Comparison</h2>
                </div>
                <div className="mh-comparison-stack">
                  {comparisonLevels.map(level => (
                    <div className="mh-comparison-row" key={level.label}>
                      <CompareLegend lineLabel="Your Score %" barLabel={`${level.label} Subject Average %`} />
                      <div className="mh-your-score-note" style={{ fontSize: '12px' }}>
                        {yourScoreLine}
                      </div>
                      <div className="mh-chart-block">
                        <PercentBarChart width={620} height={212} showLine items={sectionComparisonItemsFor(level.stats)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <NepFooter />
              <MhPageNumber page={5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MhCandidateReport
